// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { addToChainAfter, addToEndOfChain } from "@insite/client-framework/HandlerCreator";
import { chain, RequestDataFromApi } from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import LoadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import loadCustomerPartNumbers from "@vanmeter/App/Handlers/CPN/loadCustomerPartNumbers";
import { xIsAuthenticated } from "@vanmeter/App/Store/Selectors/GeneralSelectors";

export const UpdatePrice: typeof RequestDataFromApi = async props => {
    const isAuthenticated = xIsAuthenticated(props.getState());
    if (!isAuthenticated || !props.apiResult?.products || props.apiResult.products.length === 0) {
        return;
    }

    let loadedPricing = false;
    props.dispatch(
        loadRealTimePricing({
            productPriceParameters: props.apiResult.products!.map(o => {
                return {
                    productId: o.id! || "",
                    unitOfMeasure: o.properties.uom || "",
                    qtyOrdered: o.properties.minimumOrderQty || "1",
                } as any;
            }),
            onComplete: (realTimePricingProps: any) => {
                if (realTimePricingProps?.apiResult) {
                    realTimePricingProps.apiResult.realTimePricingResults?.forEach((pricing: any) => {
                        props.apiResult.products = props.apiResult.products!.map(product => {
                            if (
                                product.id === pricing.productId &&
                                product.properties["uom"] === pricing.unitOfMeasure
                            ) {
                                product.properties["pricing"] = pricing;
                            }
                            return product;
                        });
                    });
                } else if (realTimePricingProps.error) {
                    props.apiResult.products = props.apiResult.products!.map(product => {
                        product.properties["failedToLoadPricing"] = "true";
                        return product;
                    });
                }
                loadedPricing = true;
            },
        }),
    );
    await waitFor(() => !!loadedPricing);
};

export const LoadInventory: typeof RequestDataFromApi = async props => {
    if (!props.apiResult?.products || props.apiResult.products.length === 0) {
        return;
    }
    let loadedInventory = false;

    props.dispatch(
        LoadRealTimeInventory({
            productIds: props.apiResult.products.map(p => p.id || "").filter(Boolean),
            onSuccess: result => {
                result.realTimeInventoryResults?.forEach(inventory => {
                    props.apiResult.products = props.apiResult.products!.map(product => {
                        if (product?.id === inventory.productId) {
                            product.properties["inventory"] = inventory as any;
                        }
                        delete product.properties["failedToLoadInventory"];
                        return product;
                    });
                });
                loadedInventory = true;
            },
            onError: result => {
                props.apiResult.products?.every(({ product }: any) => {
                    if (product) {
                        product.properties["failedToLoadInventory"] = true;
                        delete product.properties["inventory"];
                    }
                    loadedInventory = true;
                    return product;
                });
            },
        }),
    );
    await waitFor(() => !!loadedInventory);
};

// XNG-Changes: CPN - Search auto complete - design
const xloadCustomerPartNumbers: typeof RequestDataFromApi = props => {
    const isAuth = xIsAuthenticated(props.getState());

    if (!props.apiResult?.products || props.apiResult.products.length === 0 || !isAuth) {
        return;
    }
    const productIds = props.apiResult.products.map(product => product.id);

    props.dispatch(
        loadCustomerPartNumbers({
            productIds,
        }),
    );
};

addToEndOfChain(chain, xloadCustomerPartNumbers);
addToChainAfter(chain, RequestDataFromApi, UpdatePrice);
addToChainAfter(chain, UpdatePrice, LoadInventory);
